/**
 * Long-press as the touch world's right-click. Android answers a long
 * press with a native contextmenu event, which the right-click handlers
 * already listen for; iOS Safari never sends one, so a timer stands in.
 * The two cannot land the action twice: a native contextmenu arriving
 * first cancels the pending timer (the timer is set a shade slower than
 * Android's own delay so the native path reliably wins where it exists),
 * and once the timer has fired, any late contextmenu — and the tap that a
 * released hold can still mint — is swallowed, so a look is not followed
 * by a walk nor a drop by a use.
 *
 * `want` narrows which presses count: given, a press it says no to is
 * not a gesture at all — no timer, no swallow — and the element's other
 * listeners see it as they would any tap. A panel is held by its title
 * and its frame, and a long press on a bag square inside it has to stay
 * the square's own; the panel listens for the hold, and `want` is how it
 * keeps out of the body's way.
 */

const PRESS_MS = 600; // just past Android's own long-press delay
const SLOP_PX = 12; // a resting finger drifts; a drag is a cancel

/**
 * Where a press landed and what was held with it. The keys and the button
 * are only ever set by a mouse's right-click: a finger has neither.
 */
export interface PressAt {
  clientX: number;
  clientY: number;
  shiftKey: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  button?: number;
}

/**
 * A desktop right-click is the long press too, since 2026-09-27: "make
 * right click do whatever long press does", and "make right click the same
 * as long press everywhere". It arrives as a contextmenu with no finger
 * down, and fires exactly what the hold fires, with the modifiers and the
 * button it was pressed with — a finger's press carries no button — so a
 * caller can still give Ctrl a meaning of its own, or tell a click from a
 * finger where the two have to be carried out differently. `mouse: false`
 * leaves the right-click alone.
 */
/**
 * When a finger's long press last fired, so what it opens can tell a held
 * finger from a mouse's right-click (2026-10-01: "Don't autofocus on the
 * count input field when long pressing an item in the inventory"). Only
 * the timer, which a mouse never starts, sets it.
 */
export let longPressedAt = 0;

export function longPress(
  el: HTMLElement,
  fire: (at: PressAt) => void,
  want?: (ev: PointerEvent) => boolean,
  opts: { mouse?: boolean } = {},
) {
  const mouse = opts.mouse ?? true;
  let timer = 0;
  let id = -1;
  let sx = 0,
    sy = 0;
  let holding = false; // the timer has fired and the finger is still down
  let swallowUntil = 0;
  const clear = () => {
    if (timer) clearTimeout(timer);
    timer = 0;
    id = -1;
  };
  // The long press has happened, however it was noticed. The finger is
  // still down at this point, so the pointer is left as it is for the
  // release to find: `holding` is what swallows the tap that letting go
  // can still mint, and it is pointerup that turns it into the short
  // window afterwards. Only the timer is dropped, since one of the two
  // ways in has already fired.
  const trigger = () => {
    // A press on an element that has left the page by the time the timer
    // runs is no press. The bag draws its squares afresh whenever a count
    // changes, so a tap on a potion that lands as the last one's drink
    // comes back is a press on a square that is gone: the release goes to
    // the new square under the finger, this element never hears it, and
    // the timer used to fire anyway and open the quantity dialog for a
    // quick tap (2026-10-09: "Can clicking the potion quickly in
    // succession bring up the context menu on iPhone?").
    if (el.isConnected === false) {
      clear();
      return;
    }
    longPressedAt = performance.now();
    if (timer) clearTimeout(timer);
    timer = 0;
    holding = true;
    navigator.vibrate?.(35);
    fire({ clientX: sx, clientY: sy, shiftKey: false });
  };
  // The fingers down on the element now. A second one landing is a pinch
  // or a two-finger pan, never a long press (2026-09-28: "Make two finger
  // drag pan"): two fingers held still for a moment before they move off
  // together used to be read as a hold by whichever landed last.
  const down = new Set<number>();
  el.addEventListener("pointerdown", (ev: PointerEvent) => {
    if (ev.pointerType === "mouse") return;
    down.add(ev.pointerId);
    clear();
    if (down.size > 1) return;
    swallowUntil = 0; // a new gesture's tap is its own, not the hold's echo
    if (want && !want(ev)) return;
    id = ev.pointerId;
    sx = ev.clientX;
    sy = ev.clientY;
    holding = false;
    timer = window.setTimeout(trigger, PRESS_MS);
  });
  el.addEventListener("pointermove", (ev: PointerEvent) => {
    // Once the hold has fired the finger is free to go where it likes —
    // on the world it is a stick from there (2026-10-12: "Allow long
    // pressing then dragging to move like a joystick") — and the pointer
    // is kept, so the release still finds it and swallows its echo.
    if (ev.pointerId !== id || holding) return;
    if (Math.hypot(ev.clientX - sx, ev.clientY - sy) > SLOP_PX) clear();
  });
  const done = (ev: PointerEvent) => {
    down.delete(ev.pointerId);
    if (ev.pointerId !== id) return;
    if (holding) swallowUntil = performance.now() + 800;
    holding = false;
    clear();
  };
  el.addEventListener("pointerup", done);
  el.addEventListener("pointercancel", done);
  const stale = () => holding || performance.now() < swallowUntil;
  el.addEventListener(
    "contextmenu",
    (e) => {
      if (stale()) {
        e.preventDefault();
        e.stopImmediatePropagation();
      } else if (id !== -1) {
        // Android's own answer to a long press, arriving before the timer
        // that stands in for it elsewhere. It is the same gesture, so it
        // ends in the same place rather than being let through to the
        // element's own oncontextmenu: a touchscreen that took the mouse's
        // path on one platform and the long press's on the other is
        // exactly the crack a touch-only action falls down. The event goes
        // no further, and the gesture is the long press it was.
        e.preventDefault();
        e.stopImmediatePropagation();
        trigger();
      } else if (mouse && (!want || want(e as PointerEvent))) {
        // A right-click, or the keyboard's menu key: no finger is down, so
        // it is nothing the timer knows about. Nothing to swallow after it
        // either, since no tap follows a click. `want` is asked of it as it
        // is of a press, so a panel held only by its title does not take
        // the right-click meant for a bag square inside it: this listener
        // is on the panel and runs, capturing, before the square's own.
        e.preventDefault();
        e.stopImmediatePropagation();
        fire({ clientX: e.clientX, clientY: e.clientY, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, altKey: e.altKey, button: e.button });
      }
    },
    true,
  );
  for (const type of ["click", "mousedown", "mouseup"] as const) {
    el.addEventListener(
      type,
      (e) => {
        if (stale()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true,
    );
  }
}

/** How far a finger may drift on the world before it is a drag rather than
 *  a tap, in CSS pixels: a shade under a long press's, so a hold that
 *  drifts is still a hold. */
export const TOUCH_SLOP = 8;

const STICK_DIRS = ["e", "se", "s", "sw", "w", "nw", "n", "ne"];

/**
 * The way a finger dragged dx, dy screen pixels from where it landed points,
 * as a step: one of eight, each a slice of the circle a quarter of a right
 * angle either side of its direction, or null while it is still inside the
 * slop (2026-09-28: "Make single tap and drag make the player step in the
 * direction"). Screen y runs down the page, which is south.
 */
export function stickDirFor(dx: number, dy: number): string | null {
  if (Math.hypot(dx, dy) <= TOUCH_SLOP) return null;
  const slice = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  return STICK_DIRS[(slice + 8) % 8];
}

/** Where two fingers are: how far apart, and the point halfway between. */
export interface TwoFingers {
  gap: number;
  mx: number;
  my: number;
}

/** Two touches read as a pair. */
export function twoFingers(a: { clientX: number; clientY: number }, b: { clientX: number; clientY: number }): TwoFingers {
  return {
    gap: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
    mx: (a.clientX + b.clientX) / 2,
    my: (a.clientY + b.clientY) / 2,
  };
}

/** A quarter again as far apart, or a quarter nearer, buys a zoom rung. */
export const PINCH_STEP = 1.25;

/** What a two-finger gesture is: a walk, a zoom, or not yet either. */
export type TwoFingerMode = "walk" | "zoom" | null;

/**
 * Which two fingers that landed at `start` and are now at `now` are doing.
 * Dragged together they walk, the way their middle went (2026-10-05: "Two
 * finger drag should step"); spread or drawn in they zoom. A gesture is
 * one or the other and is settled once, by its first clear movement, so a
 * pinch never sets the character walking and a walk never zooms the view:
 * the gap changing by more than the middle has moved is a zoom — a pinch
 * with one finger held still moves the middle half as far as it moves the
 * gap — and the middle moving is a walk. Inside the slop it is still
 * neither.
 */
export function twoFingerMode(start: TwoFingers, now: TwoFingers): TwoFingerMode {
  const moved = Math.hypot(now.mx - start.mx, now.my - start.my);
  const spread = Math.abs(now.gap - start.gap);
  if (spread > TOUCH_SLOP && spread >= moved) return "zoom";
  if (moved > TOUCH_SLOP) return "walk";
  return null;
}

/**
 * The zoom two fingers now at `now` ask for: a rung when the gap between
 * them has grown or shrunk by PINCH_STEP (-1 in, towards the fingers
 * spreading, +1 out). `gapFrom` is the gap the next rung is measured from:
 * it moves only when a rung is taken, so a slow pinch keeps stepping. They
 * panned the map as well until 2026-10-05, when a two-finger drag became a
 * walk (see twoFingerMode) and one finger took the pan.
 */
export function twoFingerStep(gapFrom: number, now: TwoFingers): { zoom: -1 | 0 | 1; gapFrom: number } {
  const ratio = now.gap / Math.max(1, gapFrom);
  const zoom = ratio > PINCH_STEP ? -1 : ratio < 1 / PINCH_STEP ? 1 : 0;
  return { zoom, gapFrom: zoom ? now.gap : gapFrom };
}
